import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  LogOut,
  MapPin,
  Phone,
  Baby,
  Truck,
  RefreshCw,
  CheckCircle2,
  Circle,
  Home,
  AlertTriangle,
  Map as MapIcon,
  List as ListIcon,
  UserX,
  Gauge,
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import DriverRouteMap from '@/components/DriverRouteMap';
import { DriverExtraLinks } from '@/components/DriverExtraLinks';
import API from '@/config/api';

const STATUS_META = {
  waiting: {
    label: 'Waiting',
    color: 'bg-slate-100 text-slate-700 border-slate-200',
    icon: Circle,
  },
  onboard: {
    label: 'On Van',
    color: 'bg-amber-100 text-amber-800 border-amber-200',
    icon: Truck,
  },
  delivered: {
    label: 'Delivered',
    color: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    icon: CheckCircle2,
  },
};

const StatusBadge = ({ status }) => {
  const meta = STATUS_META[status] || STATUS_META.waiting;
  const Icon = meta.icon;
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${meta.color}`}
      data-testid={`driver-child-status-${status}`}
    >
      <Icon className="w-3.5 h-3.5" />
      {meta.label}
    </span>
  );
};

const DriverDashboard = () => {
  const navigate = useNavigate();
  const { user, logout, getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(true);
  const [route, setRoute] = useState({ children: [], manifest: {}, driver: null, date: '' });
  const [direction, setDirection] = useState('pickup'); // pickup | dropoff
  const [busyChildId, setBusyChildId] = useState(null);
  const [view, setView] = useState('list'); // list | map

  // Speed tracking
  const [transportSettings, setTransportSettings] = useState({
    speed_limit_mph: 55,
    ping_interval_sec: 10,
    speedometer_visible: true,
    hard_brake_threshold_g: 0.45,
    harsh_turn_threshold_g: 0.40,
    motion_detection_enabled: true,
  });
  const [currentSpeed, setCurrentSpeed] = useState(null); // mph, null = not moving / no GPS
  const [geoError, setGeoError] = useState(null);
  const [overLimit, setOverLimit] = useState(false);
  // Motion detection
  const [motionGranted, setMotionGranted] = useState(
    typeof window !== 'undefined' && typeof DeviceMotionEvent !== 'undefined' && typeof DeviceMotionEvent.requestPermission !== 'function'
  );
  const [motionNeedsPermission, setMotionNeedsPermission] = useState(
    typeof window !== 'undefined' && typeof DeviceMotionEvent !== 'undefined' && typeof DeviceMotionEvent.requestPermission === 'function'
  );
  const [recentMotionEvent, setRecentMotionEvent] = useState(null); // {type, g, at}

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/drivers/my-route`, getAuthHeaders());
      setRoute(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load your route');
    } finally {
      setLoading(false);
    }
  };

  const loadSettings = async () => {
    try {
      const res = await axios.get(`${API}/transport/settings`, getAuthHeaders());
      setTransportSettings(res.data);
    } catch (err) {
      // keep defaults
    }
  };

  useEffect(() => {
    load();
    loadSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Watch GPS + periodically POST speed to backend
  useEffect(() => {
    if (typeof window === 'undefined' || !('geolocation' in navigator)) {
      setGeoError('Location not supported on this device');
      return;
    }
    let lastPingAt = 0;
    let lastPosition = null;
    const intervalMs = (transportSettings.ping_interval_sec || 10) * 1000;

    const watchId = navigator.geolocation.watchPosition(
      (position) => {
        setGeoError(null);
        const { latitude, longitude, speed, heading, accuracy } = position.coords;
        lastPosition = { latitude, longitude, speed, heading, accuracy };
        // Browser speed is m/s. Convert to mph for display.
        const mps = speed === null || speed === undefined || speed < 0 ? 0 : speed;
        const mph = Math.round(mps * 2.23694 * 10) / 10;
        setCurrentSpeed(mph);
        setOverLimit(mph > (transportSettings.speed_limit_mph || 55));
      },
      (err) => {
        setGeoError(err.message || 'Could not read location');
        setCurrentSpeed(null);
      },
      { enableHighAccuracy: true, maximumAge: 2000, timeout: 15000 }
    );

    const pingTimer = setInterval(async () => {
      const now = Date.now();
      if (!lastPosition || now - lastPingAt < intervalMs - 100) return;
      lastPingAt = now;
      try {
        await axios.post(
          `${API}/transport/location-ping`,
          {
            latitude: lastPosition.latitude,
            longitude: lastPosition.longitude,
            speed_mps: lastPosition.speed,
            heading: lastPosition.heading,
            accuracy: lastPosition.accuracy,
            timestamp: new Date().toISOString(),
          },
          getAuthHeaders()
        );
      } catch (e) {
        // silent — don't block the UI
      }
    }, Math.max(2000, intervalMs / 2));

    return () => {
      navigator.geolocation.clearWatch(watchId);
      clearInterval(pingTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transportSettings.ping_interval_sec, transportSettings.speed_limit_mph]);

  // DeviceMotion → detect hard-brake / harsh-turn
  // Approach: `event.acceleration` is gravity-free (x,y,z in m/s²). We watch
  // the recent 1 s window, compute dominant axis + magnitude, and if a spike
  // exceeds the center's threshold we fire an event (debounced client-side too).
  useEffect(() => {
    if (!motionGranted) return;
    if (!transportSettings.motion_detection_enabled) return;
    if (typeof window === 'undefined' || typeof DeviceMotionEvent === 'undefined') return;

    const G = 9.80665;
    const brakeG = transportSettings.hard_brake_threshold_g || 0.45;
    const turnG = transportSettings.harsh_turn_threshold_g || 0.40;

    // Simple low-pass to kill phone wiggle noise
    const alpha = 0.3;
    let lp = { x: 0, y: 0, z: 0 };
    let lastFiredAt = { hard_brake: 0, harsh_turn: 0, hard_accel: 0, jolt: 0 };
    const COOLDOWN_MS = 3000;

    const fire = async (eventType, gForce, axis) => {
      const now = Date.now();
      if (now - (lastFiredAt[eventType] || 0) < COOLDOWN_MS) return;
      lastFiredAt[eventType] = now;
      setRecentMotionEvent({ type: eventType, g: gForce, at: now });
      // Clear banner after 4 s
      setTimeout(() => {
        setRecentMotionEvent(prev => (prev && Date.now() - prev.at > 3500 ? null : prev));
      }, 4100);
      try {
        // read last position from navigator (optional)
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            axios.post(
              `${API}/transport/motion-event`,
              {
                event_type: eventType,
                g_force: gForce,
                axis,
                latitude: pos.coords.latitude,
                longitude: pos.coords.longitude,
                speed_mph: currentSpeed,
                timestamp: new Date().toISOString(),
              },
              getAuthHeaders()
            ).catch(() => {});
          },
          () => {
            axios.post(
              `${API}/transport/motion-event`,
              {
                event_type: eventType,
                g_force: gForce,
                axis,
                speed_mph: currentSpeed,
                timestamp: new Date().toISOString(),
              },
              getAuthHeaders()
            ).catch(() => {});
          },
          { maximumAge: 5000, timeout: 1500 }
        );
      } catch { /* noop */ }
    };

    const handler = (e) => {
      const a = e.acceleration;
      if (!a || (a.x === null && a.y === null && a.z === null)) return;
      // Low-pass
      lp.x = alpha * (a.x || 0) + (1 - alpha) * lp.x;
      lp.y = alpha * (a.y || 0) + (1 - alpha) * lp.y;
      lp.z = alpha * (a.z || 0) + (1 - alpha) * lp.z;

      const absX = Math.abs(lp.x);
      const absY = Math.abs(lp.y);
      const absZ = Math.abs(lp.z);
      const magnitude = Math.sqrt(lp.x * lp.x + lp.y * lp.y + lp.z * lp.z);
      const magG = magnitude / G;

      // Dominant axis — rough classification without known orientation.
      // Heuristic: biggest horizontal component wins; if the device is mounted
      // vertically (dashboard) z (phone-forward) usually means front/back.
      const horizontal = Math.max(absX, absY) / G;
      const forwardAxis = absZ / G;

      // Harsh turn: side-to-side spike dominates
      if (horizontal >= turnG && horizontal >= forwardAxis * 1.2) {
        fire('harsh_turn', +horizontal.toFixed(2), absX >= absY ? 'x' : 'y');
        return;
      }
      // Hard brake / hard acceleration: forward-back spike dominates.
      // Without a signed vehicle axis we can't tell brake vs accel for sure,
      // but the current speed trend hints: if we were moving and the spike
      // is forward-axis dominant, it's almost always a brake.
      if (forwardAxis >= brakeG && forwardAxis >= horizontal * 1.2) {
        const type = (currentSpeed || 0) > 5 ? 'hard_brake' : 'jolt';
        fire(type, +forwardAxis.toFixed(2), 'z');
        return;
      }
      // Big combined jolt (pothole, speed bump, collision candidate)
      if (magG >= Math.max(brakeG, turnG) * 1.4) {
        fire('jolt', +magG.toFixed(2), null);
      }
    };

    window.addEventListener('devicemotion', handler);
    return () => window.removeEventListener('devicemotion', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [motionGranted, transportSettings.motion_detection_enabled, transportSettings.hard_brake_threshold_g, transportSettings.harsh_turn_threshold_g, currentSpeed]);

  const requestMotionPermission = async () => {
    try {
      // iOS Safari — must be called from a user gesture
      const res = await DeviceMotionEvent.requestPermission();
      if (res === 'granted') {
        setMotionGranted(true);
        setMotionNeedsPermission(false);
      } else {
        toast.error('Motion sensors permission denied');
      }
    } catch (e) {
      toast.error('Could not enable motion sensors');
    }
  };

  const cycleStatus = async (child) => {
    setBusyChildId(child.id);
    try {
      const res = await axios.post(
        `${API}/drivers/toggle-status`,
        { child_id: child.id, direction },
        getAuthHeaders(),
      );
      setRoute((prev) => ({
        ...prev,
        manifest: {
          ...prev.manifest,
          [child.id]: { ...(prev.manifest[child.id] || {}), status: res.data.status, direction, not_riding: false },
        },
      }));
      toast.success(`${child.display_name || child.first_name || 'Child'}: ${STATUS_META[res.data.status]?.label || res.data.status}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not update status');
    } finally {
      setBusyChildId(null);
    }
  };

  const toggleSkip = async (child) => {
    setBusyChildId(child.id);
    try {
      const res = await axios.post(
        `${API}/drivers/skip-child`,
        { child_id: child.id },
        getAuthHeaders(),
      );
      setRoute((prev) => ({
        ...prev,
        manifest: {
          ...prev.manifest,
          [child.id]: {
            ...(prev.manifest[child.id] || {}),
            not_riding: res.data.not_riding,
            status: res.data.not_riding ? (prev.manifest[child.id]?.status || 'waiting') : 'waiting',
          },
        },
      }));
      toast.success(
        `${child.display_name || 'Child'}: ${res.data.not_riding ? 'Marked absent today' : 'Back on the route'}`,
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not update');
    } finally {
      setBusyChildId(null);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const activeChildren = route.children.filter((c) => !route.manifest[c.id]?.not_riding);
  const total = activeChildren.length;
  const onboard = activeChildren.filter((c) => route.manifest[c.id]?.status === 'onboard').length;
  const delivered = activeChildren.filter((c) => route.manifest[c.id]?.status === 'delivered').length;
  const absent = route.children.length - total;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="driver-dashboard">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white border-b border-border">
        <div className="max-w-3xl mx-auto px-4 h-16 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
              <Truck className="w-5 h-5 text-primary" />
            </div>
            <div className="min-w-0">
              <div className="font-heading font-bold text-base truncate" data-testid="driver-name">
                {user?.name || 'Driver'}
              </div>
              <div className="text-xs text-muted-foreground">Van Manifest</div>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <DriverExtraLinks />
            <Button
              variant="ghost"
              size="icon"
              onClick={load}
              disabled={loading}
              data-testid="driver-refresh-btn"
              aria-label="Refresh"
            >
              <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleLogout}
              data-testid="driver-logout-btn"
              aria-label="Logout"
            >
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-4 space-y-4">
        {/* Speedometer */}
        {transportSettings.speedometer_visible && (
          <div
            className={`rounded-xl border-2 p-3 flex items-center justify-between transition-colors ${
              overLimit ? 'border-red-500 bg-red-50 animate-pulse' : 'border-primary/30 bg-primary/5'
            }`}
            data-testid="driver-speedometer"
          >
            <div className="flex items-center gap-3">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center ${overLimit ? 'bg-red-500 text-white' : 'bg-primary/15 text-primary'}`}>
                <Gauge className="w-6 h-6" />
              </div>
              <div>
                <div className="flex items-baseline gap-2">
                  <span
                    className={`text-3xl font-bold tabular-nums ${overLimit ? 'text-red-600' : 'text-gray-900'}`}
                    data-testid="driver-current-speed"
                  >
                    {currentSpeed === null ? '—' : Math.round(currentSpeed)}
                  </span>
                  <span className="text-sm text-muted-foreground">mph</span>
                </div>
                <p className="text-xs text-muted-foreground">
                  Limit: <strong>{transportSettings.speed_limit_mph} mph</strong>
                  {geoError && <span className="text-red-600 ml-2">• {geoError}</span>}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* iOS motion-sensor permission button (noop on Android/desktop) */}
              {motionNeedsPermission && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={requestMotionPermission}
                  className="h-8 text-xs"
                  data-testid="enable-motion-btn"
                >
                  <AlertTriangle className="w-3 h-3 mr-1" />
                  Enable motion
                </Button>
              )}
              {overLimit && (
                <div className="text-right">
                  <span className="text-xs font-bold text-red-700 uppercase tracking-wide">Over limit</span>
                  <p className="text-[11px] text-red-600">Please slow down</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Harsh-motion flash banner */}
        {recentMotionEvent && (
          <div
            className="rounded-xl border-2 border-orange-400 bg-orange-50 p-3 flex items-center justify-between animate-pulse"
            data-testid="driver-motion-banner"
          >
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-orange-600" />
              <div>
                <p className="text-sm font-semibold text-orange-900">
                  {recentMotionEvent.type === 'hard_brake' && 'Hard brake detected'}
                  {recentMotionEvent.type === 'harsh_turn' && 'Harsh turn detected'}
                  {recentMotionEvent.type === 'hard_accel' && 'Hard acceleration'}
                  {recentMotionEvent.type === 'jolt' && 'Big jolt — pothole or bump?'}
                </p>
                <p className="text-xs text-orange-800">Peak {recentMotionEvent.g}g — logged for the center</p>
              </div>
            </div>
          </div>
        )}

        {/* Direction toggle */}
        <div className="bg-white rounded-xl border border-border p-1 flex" data-testid="driver-direction-toggle">
          <button
            onClick={() => setDirection('pickup')}
            className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-colors ${
              direction === 'pickup' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground'
            }`}
            data-testid="driver-direction-pickup"
          >
            AM Pickup → Center
          </button>
          <button
            onClick={() => setDirection('dropoff')}
            className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-colors ${
              direction === 'dropoff' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground'
            }`}
            data-testid="driver-direction-dropoff"
          >
            PM Dropoff → Home
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-2">
          <Card className="card-clay">
            <CardContent className="p-3 text-center">
              <div className="text-2xl font-bold" data-testid="driver-stat-total">{total}</div>
              <div className="text-xs text-muted-foreground mt-0.5">Riding</div>
            </CardContent>
          </Card>
          <Card className="card-clay">
            <CardContent className="p-3 text-center">
              <div className="text-2xl font-bold text-amber-600" data-testid="driver-stat-onboard">{onboard}</div>
              <div className="text-xs text-muted-foreground mt-0.5">On Van</div>
            </CardContent>
          </Card>
          <Card className="card-clay">
            <CardContent className="p-3 text-center">
              <div className="text-2xl font-bold text-emerald-600" data-testid="driver-stat-delivered">{delivered}</div>
              <div className="text-xs text-muted-foreground mt-0.5">Delivered</div>
            </CardContent>
          </Card>
          <Card className="card-clay">
            <CardContent className="p-3 text-center">
              <div className="text-2xl font-bold text-slate-500" data-testid="driver-stat-absent">{absent}</div>
              <div className="text-xs text-muted-foreground mt-0.5">Absent</div>
            </CardContent>
          </Card>
        </div>

        {/* View toggle: List / Map */}
        <div className="bg-white rounded-xl border border-border p-1 flex" data-testid="driver-view-toggle">
          <button
            onClick={() => setView('list')}
            className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors inline-flex items-center justify-center gap-1.5 ${
              view === 'list' ? 'bg-primary/10 text-primary' : 'text-muted-foreground'
            }`}
            data-testid="driver-view-list"
          >
            <ListIcon className="w-4 h-4" /> List
          </button>
          <button
            onClick={() => setView('map')}
            className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors inline-flex items-center justify-center gap-1.5 ${
              view === 'map' ? 'bg-primary/10 text-primary' : 'text-muted-foreground'
            }`}
            data-testid="driver-view-map"
          >
            <MapIcon className="w-4 h-4" /> Route Map
          </button>
        </div>

        {/* Children list */}
        {loading && (
          <div className="text-center py-12 text-muted-foreground" data-testid="driver-loading">
            Loading your route…
          </div>
        )}

        {!loading && route.children.length === 0 && (
          <Card className="card-clay">
            <CardContent className="p-8 text-center space-y-2">
              <AlertTriangle className="w-8 h-8 text-amber-500 mx-auto" />
              <div className="font-semibold">No children assigned</div>
              <div className="text-sm text-muted-foreground">
                Ask your admin to assign children to your van from the Drivers page.
              </div>
            </CardContent>
          </Card>
        )}

        {!loading && route.children.length > 0 && view === 'map' && (
          <DriverRouteMap
            stops={route.children.filter((c) => !route.manifest[c.id]?.not_riding)}
            direction={direction}
          />
        )}

        {!loading && view === 'list' && route.children.map((child) => {
          const status = route.manifest[child.id]?.status || 'waiting';
          const notRiding = route.manifest[child.id]?.not_riding || false;
          const childName = child.display_name || `${child.first_name || ''} ${child.last_name || ''}`.trim() || child.name || 'Child';
          const address = direction === 'pickup' ? child.pickup_address : child.dropoff_address;
          const label = direction === 'pickup' ? child.pickup_label : child.dropoff_label;
          const scheduledTime = direction === 'pickup' ? child.pickup_time : child.dropoff_time;
          // Navigate button uses maps
          const mapsHref = address
            ? `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}`
            : null;
          return (
            <Card
              key={child.id}
              className={`card-clay ${notRiding ? 'opacity-60' : ''}`}
              data-testid={`driver-child-card-${child.id}`}
            >
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between gap-2">
                  <CardTitle className="text-lg flex items-center gap-2 min-w-0">
                    {child.stop_order && (
                      <span
                        className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-primary text-primary-foreground text-xs font-bold flex-shrink-0"
                        data-testid={`driver-child-stop-num-${child.id}`}
                      >
                        {child.stop_order}
                      </span>
                    )}
                    <Baby className="w-5 h-5 text-primary flex-shrink-0" />
                    <span className="truncate">{childName}</span>
                    {scheduledTime && (
                      <span
                        className="ml-1 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md bg-primary/10 text-primary text-xs font-medium flex-shrink-0"
                        data-testid={`driver-child-scheduled-${child.id}`}
                      >
                        {(() => {
                          const [h, m] = scheduledTime.split(':').map(Number);
                          const dt = new Date();
                          dt.setHours(h, m, 0, 0);
                          return dt.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
                        })()}
                      </span>
                    )}
                  </CardTitle>
                  {notRiding ? (
                    <Badge className="bg-slate-200 text-slate-700 hover:bg-slate-200" data-testid={`driver-child-absent-${child.id}`}>
                      Not riding today
                    </Badge>
                  ) : (
                    <StatusBadge status={status} />
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-3 pt-0">
                {address ? (
                  <div className="space-y-1">
                    {label && (
                      <div className="flex items-center gap-1.5 text-xs font-medium text-primary">
                        {direction === 'pickup' ? 'Pick up from' : 'Drop off at'}: {label}
                      </div>
                    )}
                    <a
                      href={mapsHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-start gap-2 text-sm text-foreground hover:underline"
                      data-testid={`driver-child-address-${child.id}`}
                    >
                      {direction === 'pickup' ? (
                        <Home className="w-4 h-4 flex-shrink-0 mt-0.5 text-muted-foreground" />
                      ) : (
                        <MapPin className="w-4 h-4 flex-shrink-0 mt-0.5 text-muted-foreground" />
                      )}
                      <span className="break-words">{address}</span>
                    </a>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-xs text-muted-foreground italic">
                    <MapPin className="w-3.5 h-3.5" /> No {direction === 'pickup' ? 'pickup' : 'dropoff'} address — call the parent.
                  </div>
                )}

                <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
                  {child.classroom && (
                    <Badge variant="secondary" data-testid={`driver-child-classroom-${child.id}`}>
                      {child.classroom}
                    </Badge>
                  )}
                  {child.parent_name && (
                    <span>Parent: <span className="text-foreground">{child.parent_name}</span></span>
                  )}
                </div>

                {child.parent_phone && (
                  <a
                    href={`tel:${child.parent_phone}`}
                    className="inline-flex items-center gap-1.5 text-sm text-primary underline"
                    data-testid={`driver-call-parent-${child.id}`}
                  >
                    <Phone className="w-4 h-4" />
                    {child.parent_phone}
                  </a>
                )}

                {(child.allergies || child.medical_notes) && !notRiding && (
                  <div className="text-xs bg-amber-50 border border-amber-200 rounded-md p-2">
                    <div className="flex items-center gap-1 font-semibold text-amber-800">
                      <AlertTriangle className="w-3.5 h-3.5" /> Alerts
                    </div>
                    {child.allergies && <div className="mt-1 text-amber-900">Allergies: {child.allergies}</div>}
                    {child.medical_notes && <div className="text-amber-900">Medical: {child.medical_notes}</div>}
                  </div>
                )}

                <div className="flex gap-2">
                  <Button
                    className="flex-1 btn-primary"
                    onClick={() => cycleStatus(child)}
                    disabled={busyChildId === child.id || notRiding}
                    data-testid={`driver-toggle-btn-${child.id}`}
                  >
                    {busyChildId === child.id ? 'Saving…' : `Mark ${
                      status === 'waiting' ? 'On Van' :
                      status === 'onboard' ? (direction === 'pickup' ? 'Delivered to Center' : 'Delivered Home') :
                      'Waiting'
                    }`}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => toggleSkip(child)}
                    disabled={busyChildId === child.id}
                    data-testid={`driver-skip-btn-${child.id}`}
                    className="whitespace-nowrap"
                  >
                    <UserX className="w-4 h-4 mr-1" />
                    {notRiding ? 'Riding' : 'Skip'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}

        <div className="text-center text-xs text-muted-foreground pt-4 pb-8">
          {route.date && <>Manifest for {route.date}</>}
        </div>
      </main>
    </div>
  );
};

export default DriverDashboard;
